import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnboardingFacade } from '../../../../../Facades/onboarding.facade';
import { AuthFacade } from '../../../../../Facades/auth.facade';
import { ThemeService } from '../../../../Shared/Services/theme.service';
import { TranslationService } from '../../../../Shared/Services/translation.service';
import { environment } from '../../../../../../environments/environment';
import { FinalizeStep } from '../../../Interfaces/finalize-step.interface';

@Component({
  selector: 'app-all-set',
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    MatIconModule,
    TranslateModule
  ],
  templateUrl: './all-set.component.html',
  styleUrl: './all-set.component.scss'
})
export class AllSetComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private onboardingService = inject(OnboardingFacade);
  private authService = inject(AuthFacade);
  private translate = inject(TranslateService);
  private themeService = inject(ThemeService);
  private translationService = inject(TranslationService);

  state: 'completing' | 'success' | 'error' = 'completing';
  errorMessage = '';

  private sessionId: string | null = null;

  readonly steps = signal<FinalizeStep[]>(this.buildSteps());

  ngOnInit(): void {
    this.resolveEntry();
  }

  private resolveEntry(): void {
    const params = this.route.snapshot.queryParamMap;
    const checkout = params.get('checkout');
    this.sessionId = params.get('session_id');

    if (checkout !== 'success' || !this.sessionId) {
      this.handleUnauthorizedAccess();
      return;
    }

    this.verifyAndComplete();
  }

  private handleUnauthorizedAccess(): void {
    this.authService.clearAuth();
    this.router.navigate(['/login']);
  }

  private verifyAndComplete(): void {
    this.state = 'completing';
    this.steps.set(this.buildSteps());
    this.runStepProgression();

    this.onboardingService.verifyCheckout(this.sessionId!, { skipLoading: true }).subscribe({
      next: () => {
        this.completeOnboarding();
      },
      error: (error) => {
        this.state = 'error';
        this.errorMessage =
          error.message || this.translate.instant('onboarding.setup.allSet.errors.verifyFailed');
      }
    });
  }

  private completeOnboarding(): void {
    const preferences = {
      preferred_lang: this.translationService.currentLanguage(),
      preferred_theme: this.themeService.mode(),
    };

    this.onboardingService.complete(preferences, { skipLoading: true }).subscribe({
      next: () => {
        this.markAllStepsComplete();
        this.state = 'success';
        this.scheduleHandoff();
      },
      error: (error) => {
        this.state = 'error';
        this.errorMessage =
          error.message || this.translate.instant('onboarding.setup.allSet.errors.completeFailed');
      }
    });
  }

  retryCompletion(): void {
    this.verifyAndComplete();
  }

  private scheduleHandoff(): void {
    setTimeout(() => this.handOffToApp(), 6000);
  }

  private handOffToApp(): void {
    this.authService.terminateSession().subscribe({
      next: () => this.redirectToApp(),
      error: () => this.redirectToApp()
    });
  }

  private redirectToApp(): void {
    window.location.replace(environment.appURL);
  }

  private async runStepProgression(): Promise<void> {
    this.updateStep('org', 'loading');
    await this.delay(900);
    if (this.state !== 'completing') return;
    this.updateStep('org', 'completed');

    this.updateStep('roles', 'loading');
    await this.delay(900);
    if (this.state !== 'completing') return;
    this.updateStep('roles', 'completed');

    this.updateStep('finalize', 'loading');
  }

  private updateStep(id: FinalizeStep['id'], state: FinalizeStep['state']): void {
    this.steps.update((current) =>
      current.map((step) => (step.id === id ? { ...step, state } : step))
    );
  }

  private markAllStepsComplete(): void {
    this.steps.update((current) =>
      current.map((step) => ({ ...step, state: 'completed' as const }))
    );
  }

  private buildSteps(): FinalizeStep[] {
    return [
      { id: 'org', label: 'onboarding.setup.allSet.steps.org', state: 'pending' },
      { id: 'roles', label: 'onboarding.setup.allSet.steps.roles', state: 'pending' },
      { id: 'finalize', label: 'onboarding.setup.allSet.steps.finalize', state: 'pending' }
    ];
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
